/**
 * Copyright © 2026 Adnuntius AS.
 *
 * Buffers failed AdnHttp requests on 401, publishes login-required/confirmed/
 * cancelled events through the application event bus, and retries via AdnHttp
 * after loginConfirmed (re-applying auth headers).
 */
import {Injectable, Injector, OnDestroy} from '@angular/core';
import * as _ from 'lodash';
import {AppEventBusService} from '../../app/common/events/app-event-bus.service';

import {AdnHttpRequest, AdnHttpResponse, AdnHttpService} from '../util/adn-http.service';
import {AuthHttpInterceptorService} from './auth-http-interceptor.service';

export type AdnHttpConfigUpdater = (config: AdnHttpRequest) => AdnHttpRequest | false;

interface BufferedRequest {
  config: AdnHttpRequest;
  rejection: any;
  resolve: (response: AdnHttpResponse) => void;
  reject: (reason: any) => void;
}

/**
 * Set by the root AuthHttpBufferService instance so plain resource factory
 * helpers (createAdnHttpResponseSender) can buffer 401s without threading the
 * service through every factory deps list.
 */
let authHttpBufferInstance: AuthHttpBufferService | null = null;

export function getAuthHttpBuffer(): AuthHttpBufferService | null {
  return authHttpBufferInstance;
}

@Injectable({
  providedIn: 'root'
})
export class AuthHttpBufferService implements OnDestroy {
  /** Suppresses auth refresh storms while a network switch reloads the app. */
  networkSwitchInProgress = false;
  /** True while AppRuntimeLifecycleService is refreshing the access token. */
  authRefreshInProgress = false;
  /** True while bootstrap refreshes an already-expired session. */
  startupSessionRefreshInProgress = false;

  private buffer: BufferedRequest[] = [];

  constructor(
    private readonly injector: Injector,
    private readonly eventBus: AppEventBusService
  ) {
    authHttpBufferInstance = this;
  }

  ngOnDestroy(): void {
    if (authHttpBufferInstance === this) {
      authHttpBufferInstance = null;
    }
  }

  /**
   * Buffer a failed request and return a promise that settles when
   * loginConfirmed retries it or loginCancelled rejects it. Broadcasts
   * event:auth-loginRequired only when the buffer was previously empty.
   */
  bufferRequest(config: AdnHttpRequest, rejection: any): Promise<AdnHttpResponse> {
    return new Promise<AdnHttpResponse>((resolve, reject) => {
      const length = this.buffer.push({config, rejection, resolve, reject});
      if (length === 1) {
        this.eventBus.emit('event:auth-loginRequired', rejection);
      }
    });
  }

  /** Broadcast login-required without buffering (e.g. notification poll 401). */
  notifyLoginRequired(rejection?: any): void {
    this.eventBus.emit('event:auth-loginRequired', rejection);
  }

  loginConfirmed(data?: any, configUpdater?: AdnHttpConfigUpdater): void {
    this.eventBus.emit('event:auth-loginConfirmed', data);
    this.retryAll(configUpdater || ((config) => config));
  }

  loginCancelled(data?: any, reason?: any): void {
    this.rejectAll(reason);
    this.eventBus.emit('event:auth-loginCancelled', data);
  }

  private rejectAll(reason?: any): void {
    const pending = this.buffer.splice(0);
    _.forEach(pending, (item) => item.reject(reason || item.rejection));
  }

  private retryAll(updater: AdnHttpConfigUpdater): void {
    const pending = this.buffer.splice(0);
    _.forEach(pending, (item) => {
      const updated = updater(item.config);
      if (updated === false) {
        item.reject(item.rejection);
        return;
      }
      this.retryRequest(updated, item);
    });
  }

  private retryRequest(config: AdnHttpRequest, item: BufferedRequest): void {
    const adnHttp = this.injector.get(AdnHttpService);
    const authHttpInterceptor = this.injector.get(AuthHttpInterceptorService);
    const merged: AdnHttpRequest = authHttpInterceptor.request(_.assign({params: {}, headers: {}}, config));
    adnHttp.request(merged).then(
      (response) => item.resolve(response),
      (rejection) => {
        if (rejection && rejection.status === 401 && !merged.ignoreAuthModule) {
          const length = this.buffer.push(item);
          if (length === 1) {
            this.eventBus.emit('event:auth-loginRequired', rejection);
          }
          return;
        }
        item.reject(rejection);
      }
    );
  }
}
